import { isWithinRange } from 'date-fns';
import { groupBy, last, meanBy, sortBy, sumBy } from 'lodash';
import { variables } from '../../components/utils';
import {
  dataForBinaryBar as _dataForBinaryBar,
  dataForTrafficLightBar as _dataForTrafficLightBar
} from '../helpers';
import { QualaRange } from '../qualaRange';
import { DateSummary } from './dateSummary';
import { Measurement } from './measurement';
import { PercentageDateSummary } from './percentageDateSummary';
import { organize } from './utils';

type TSeriesType = 'mostRecent' | 'average' | 'sum' | 'averageAndRound';
interface MeasurementAggregateInput {
  seriesType: TSeriesType;
  aggregationType: 'average' | 'averageAndRound' | 'sum';
}

type AggregateType = 'sum' | 'average' | 'mostRecent' | 'averageAndRound';

type Trend = 'Up' | 'Down' | 'Even';

/**
 * SubjectHelper
 *
 */
export class MeasurementHelper {
  public range: QualaRange;
  public measurements: Measurement[];

  public constructor(measurements: Measurement[], range: QualaRange) {
    this.measurements = measurements;
    this.range = range;
  }

  /**
   * the previous periods Measurement helper
   *
   * Note: if these gets called frequently while building a page
   * we should memonize it or implement some kinds of caching strategy
   */
  public get previous(): MeasurementHelper {
    return new MeasurementHelper(this.measurements, this.range.previous);
  }

  /**
   * the next periods Measurement helper
   *
   * Note: if these gets called frequently while building a page
   * we should memonize it or implement some kinds of caching strategy
   */
  public get next(): MeasurementHelper {
    return new MeasurementHelper(this.measurements, this.range.next);
  }

  public aggregateMeasurements(aggregateType: AggregateType): DateSummary[] {
    const organizedPoints = organize<Measurement>(this.measurements, this.range);
    const dateSummaries: { [dateString: string]: DateSummary } = {};

    Object.keys(organizedPoints).forEach(dateString => {
      dateSummaries[dateString] = new DateSummary(
        dateString,
        organizedPoints[dateString],
        aggregateType
      );
    });

    return Object.values(dateSummaries);
  }

  /**
   * all of the measurements, with the notedAt pinned to the beginning of its
   * time scale period
   *
   * For example.  If we are viewing a year chart, the timescale is month
   * each measurement will be at the associated beginning of the period
   * so  Feb 12 --> Feb 1st
   *
   */
  public timeAlignedPoints(): Measurement[] {
    const organizedPoints = organize<Measurement>(this.measurements, this.range);
    const timeAlignedPoints: Measurement[] = [];

    Object.keys(organizedPoints).forEach(dateString => {
      organizedPoints[dateString].forEach((point: Measurement) => {
        timeAlignedPoints.push(new Measurement({ ...point, notedAt: dateString }));
      });
    });

    return timeAlignedPoints;
  }

  public aggregate(input: MeasurementAggregateInput): number {
    const { seriesType, aggregationType } = input;
    let series: DateSummary[] = [];
    if (seriesType === 'mostRecent') {
      series = this.mostRecentSeries();
    } else if (seriesType === 'average') {
      series = this.averageSeries();
    } else if (seriesType === 'sum') {
      series = this.sumSeries();
    }

    if (aggregationType === 'average') {
      return meanBy(series, 'value');
    } else if (aggregationType === 'sum') {
      return sumBy(series, 'value');
    } else {
      throw new Error('Unknown aggregation type');
    }
  }

  /**
   * compares the value to the previous value to figure out trends
   * @param input
   */
  public trend(input: MeasurementAggregateInput): Trend {
    const currentValue = this.aggregate(input);
    const previousValue = this.previous.aggregate(input);

    if (
      currentValue === undefined ||
      previousValue === undefined ||
      isNaN(currentValue) ||
      isNaN(previousValue)
    ) {
      return 'Even';
    } else if (currentValue > previousValue) {
      return 'Up';
    } else if (currentValue < previousValue) {
      return 'Down';
    } else if (currentValue === previousValue) {
      return 'Even';
    } else {
      throw new Error('Well thats odd');
    }
  }

  public percentageByDate(): PercentageDateSummary[] {
    const percentageDateSummaries: PercentageDateSummary[] = [];

    const groupedByDate = this.groupByDate();

    Object.keys(groupedByDate).map((dateString: string) => {
      percentageDateSummaries.push(
        new PercentageDateSummary(dateString, groupedByDate[dateString])
      );
    });

    return percentageDateSummaries;
  }

  public dataForTrafficLightBar() {
    return _dataForTrafficLightBar(this.groupByDate(), this.range);
  }

  public dataForBinaryBar() {
    return _dataForBinaryBar(this.groupByDate(), this.range);
  }

  public groupByDate(): { [dateString: string]: Measurement[] } {
    return groupBy(this.timeAlignedPoints(), 'notedAt');
  }

  public mostRecentSeries(): DateSummary[] {
    return this.aggregateMeasurements('mostRecent');
  }

  public averageSeries(): DateSummary[] {
    return this.aggregateMeasurements('average');
  }

  public averageAndRoundedSeries(): DateSummary[] {
    return this.aggregateMeasurements('averageAndRound');
  }

  public sumSeries(): DateSummary[] {
    return this.aggregateMeasurements('sum');
  }

  // Work with JT to make this better
  public pieChart(): { data: DateSummary[]; colorsTrafficLight: string[]; colorsBinary: string[] } {
    const measurements = this.measurementsInRange();
    const values = groupBy(measurements, 'value');

    const data: any = [];
    Object.keys(values).forEach(value => {
      const count = values[value].length;
      data.push({ x: value, y: count });
    });

    const colorsTrafficLight = data.map((row: any) => {
      if (row.x === '0') {
        return variables.yellow;
      } else if (row.x === '1') {
        return variables.green;
      }
      return variables.red;
    });

    const colorsBinary = data.map((row: any) => {
      if (row.x === '0') {
        return variables.washedBlue;
      }
      return variables.blue;
    });

    return { data, colorsTrafficLight, colorsBinary };
  }

  public measurementsInRange() {
    return this.measurements.filter(m => {
      return isWithinRange(m.date, new Date(this.range.start), new Date(this.range.end));
    });
  }

  public mostFrequentValue() {
    let value = 0;
    const groupByValue = groupBy(this.measurementsInRange(), 'value');

    Object.keys(groupByValue).forEach((val, index) => {
      const valueTotal = groupByValue[val].length;
      if (valueTotal > value || index === 0) {
        value = Number(val);
      }
    });

    return value;
  }

  public mostFrequentBinary() {
    let value: number = -1;
    const groupByValue = groupBy(this.measurementsInRange(), 'value');

    Object.keys(groupByValue).forEach((val, index) => {
      const valueTotal = groupByValue[val].length;
      if (valueTotal > value || index === 0) {
        value = Number(val);
      }
    });

    let yesNo: string;
    if (value === 0) {
      yesNo = 'no';
    } else if (value === 1) {
      yesNo = 'yes';
    } else {
      yesNo = 'n/a';
    }

    return yesNo;
  }

  public mostFrequentTrafficLight() {
    let value: number = -2;
    const groupByValue = groupBy(this.measurementsInRange(), 'value');

    Object.keys(groupByValue).forEach((val, index) => {
      const valueTotal = groupByValue[val].length;
      if (valueTotal > value || index === 0) {
        value = Number(val);
      }
    });

    let trafficLightColor: string;
    if (value === -1) {
      trafficLightColor = 'red';
    } else if (value === 0) {
      trafficLightColor = 'yellow';
    } else if (value === 1) {
      trafficLightColor = 'green';
    } else {
      trafficLightColor = 'n/a';
    }

    return trafficLightColor;
  }

  public get lastMeasurement(): Measurement {
    return this.measurements[0];
  }

  public get mostRecentMeasurement(): Measurement {
    return last(sortBy(this.measurements, m => m.notedAt)) as Measurement;
  }

  /**
   * ==================================================
   * Single Number Aggregations
   * ==================================================
   */

  /**
   * A count of all the entries in this range.
   */
  public get measurementCount(): number {
    const measurements = this.timeAlignedPoints();
    return measurements.length;
  }

  /**
   * This works out the average number per time unit in our integer charts
   * (i.e. "about 19 per day", "about 112 per month")
   *
   * TODO: Rename this thing
   */
  public get averageByTimeScale(): number {
    const total = this.aggregate({ aggregationType: 'sum', seriesType: 'sum' });
    const divisor = this.range.tickCount;
    return total / divisor;
  }
}
